import * as THREE from 'three';
import { edgeLines, setEdgeStyle } from './edges';
import type { EdgeLines } from './edges';
import { disposeLines, disposePoints, highlightLines, markerPoints, showPoints } from './marks';
import type { MarkerPoints } from './marks';
import { measureColor } from './palette';
import { PerMesh } from './perMesh';
import { modelHit } from './picking';
import { PointerClicks } from './pointerClicks';
import { SnapScope, snapEdges } from './snap';
import type { MeshEdges } from './snap';

const MARKER_PX = 10;
const PREVIEW_PX = 14;
const LINE_PX = 2;

export interface Measurement {
  distance: number;
  delta: { x: number; y: number; z: number };
}

export interface MeasureState {
  placed: number;
  measurement: Measurement | null;
}

export const IDLE_MEASURE: MeasureState = { placed: 0, measurement: null };

export interface MeasureTarget {
  point: THREE.Vector3;
  /** The edge the point snapped onto, as line segments. */
  edge: Float32Array | null;
}

export class MeasureTool {
  readonly object = new THREE.Group();
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer: PointerClicks;
  private readonly color: number;
  private readonly markers: MarkerPoints;
  private readonly preview: MarkerPoints;
  private readonly points: THREE.Vector3[] = [];
  private readonly scope = new SnapScope();
  private edges: PerMesh<MeshEdges> | null = null;
  private readonly projected = new THREE.Vector3();
  private model: THREE.Object3D | null = null;
  private clip: THREE.Plane | null = null;
  private line: EdgeLines | null = null;
  private edgeLine: EdgeLines | null = null;
  private target: MeasureTarget | null = null;
  private label: HTMLElement | null = null;
  private labelTransform: string | null = null;
  private active = false;

  constructor(
    element: HTMLElement,
    isDark: boolean,
    getCamera: () => THREE.Camera,
    isCovered: (event: PointerEvent) => boolean,
    private readonly onChange: (state: MeasureState) => void,
  ) {
    this.color = measureColor(isDark);
    this.markers = markerPoints(this.color, MARKER_PX, 2);
    this.preview = markerPoints(this.color, PREVIEW_PX, 1);
    this.object.add(this.markers, this.preview);
    this.pointer = new PointerClicks(
      element,
      isCovered,
      () => this.active,
      (rect) => {
        const target = this.targetAt(getCamera(), rect.height);
        if (target) this.place(target.point);
      },
      () => this.showTarget(null),
    );
  }

  /** Where a click would land right now, and the edge it would land on. */
  get hover(): MeasureTarget | null {
    return this.target;
  }

  setModel(model: THREE.Object3D | null): void {
    this.reset();
    this.showTarget(null);
    this.edges?.stop();
    this.edges = model ? new PerMesh(model, snapEdges(model)) : null;
    this.model = model;
    if (this.active) this.edges?.prepare();
  }

  setClip(plane: THREE.Plane | null): void {
    this.clip = plane;
    if (this.edgeLine) this.edgeLine.material.clippingPlanes = plane ? [plane] : null;
    this.invalidate();
  }

  setActive(active: boolean): void {
    this.active = active;
    this.pointer.cancelPress();
    if (active) {
      this.invalidate();
      // Building snap edges for a dense model takes long enough to drop
      // frames, so it happens once Measure is on rather than on the first hover.
      this.edges?.prepare();
      return;
    }
    this.edges?.stop();
    this.reset();
    this.showTarget(null);
  }

  setLabel(label: HTMLElement | null): void {
    this.label = label;
    this.labelTransform = null;
  }

  /** The view moved under a still pointer. */
  invalidate(): void {
    this.pointer.invalidate();
  }

  update(camera: THREE.Camera, width: number, height: number): void {
    if (this.pointer.takeStale()) {
      this.showTarget(this.active && this.pointer.hovering ? this.targetAt(camera, height) : null);
    }
    this.placeLabel(camera, width, height);
  }

  clear(): void {
    this.reset();
  }

  clearHover(): void {
    this.showTarget(null);
  }

  dispose(): void {
    this.edges?.stop();
    this.pointer.dispose();
    disposeLines(this.line);
    disposeLines(this.edgeLine);
    disposePoints(this.markers);
    disposePoints(this.preview);
    this.object.removeFromParent();
  }

  private placeLabel(camera: THREE.Camera, width: number, height: number): void {
    const label = this.label;
    if (!label) return;
    let transform = '';
    if (this.points.length === 2) {
      this.projected.addVectors(this.points[0], this.points[1]).multiplyScalar(0.5).project(camera);
      if (Math.abs(this.projected.z) <= 1) {
        const x = (this.projected.x * 0.5 + 0.5) * width;
        const y = (-this.projected.y * 0.5 + 0.5) * height;
        transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%)`;
      }
    }
    if (transform === this.labelTransform) return;
    this.labelTransform = transform;
    label.style.visibility = transform ? 'visible' : 'hidden';
    label.style.transform = transform;
  }

  private reset(): void {
    if (this.points.length === 0) return;
    this.points.length = 0;
    disposeLines(this.line);
    this.line = null;
    showPoints(this.markers, this.points);
    this.onChange(IDLE_MEASURE);
  }

  private showTarget(target: MeasureTarget | null): void {
    const edge = target?.edge ?? null;
    if (edge !== (this.target?.edge ?? null)) {
      disposeLines(this.edgeLine);
      this.edgeLine = edge ? highlightLines(edge, this.color, LINE_PX, this.clip) : null;
      if (this.edgeLine) this.object.add(this.edgeLine);
    }
    this.target = target;
    showPoints(this.preview, target ? [target.point] : []);
  }

  private targetAt(camera: THREE.Camera, heightPx: number): MeasureTarget | null {
    if (!this.model || !this.edges) return null;
    this.raycaster.setFromCamera(this.pointer.ndc, camera);
    const hit = modelHit(this.raycaster, this.model, this.clip);
    if (!hit) return null;
    const { ray } = this.raycaster;
    const { reach, accept } = this.scope.around(hit, ray, camera, heightPx, this.clip);
    const edges = this.edges.of(hit.object as THREE.Mesh);
    const snap = edges.nearest(ray, reach, accept);
    if (!snap) return { point: hit.point.clone(), edge: null };
    return { point: snap.point, edge: snap.segment === null ? null : edges.chain(snap.segment) };
  }

  private place(point: THREE.Vector3): void {
    if (this.points.length >= 2) {
      this.points.length = 0;
      disposeLines(this.line);
      this.line = null;
    }
    this.points.push(point);
    showPoints(this.markers, this.points);
    if (this.points.length < 2) {
      this.onChange({ placed: 1, measurement: null });
      return;
    }
    const [a, b] = this.points;
    const line = edgeLines(
      new THREE.BufferGeometry().setAttribute(
        'position',
        new THREE.Float32BufferAttribute([a.x, a.y, a.z, b.x, b.y, b.z], 3),
      ),
      LINE_PX,
    );
    setEdgeStyle(line, this.color, 1);
    line.material.depthTest = false;
    line.frustumCulled = false;
    line.renderOrder = 2;
    this.object.add(line);
    this.line = line;
    this.onChange({
      placed: 2,
      measurement: {
        distance: a.distanceTo(b),
        delta: { x: Math.abs(b.x - a.x), y: Math.abs(b.y - a.y), z: Math.abs(b.z - a.z) },
      },
    });
  }
}
